"use client";

import { useSession, getSession } from "next-auth/react";
import { useRouter, usePathname } from "next/navigation";
import { useEffect } from "react";
import Sidebar from "@/app/components/sidebar";
import Header from "@/app/components/header";
import Footer from "@/app/components/footer";

const PUBLIC_PATHS = ["/", "/login"];
const PUBLIC_PREFIXES = ["/copyediting/accept"];

export default function LayoutClient({ children }) {
  const { data: session, status } = useSession();
  const router = useRouter();
  const pathname = usePathname();

  const isPublicPath =
    PUBLIC_PATHS.includes(pathname) ||
    PUBLIC_PREFIXES.some((p) => pathname.startsWith(p));

  const isExpired =
    session?.expires && Date.now() >= new Date(session.expires).getTime();
  const isAuthenticated = session && !isExpired;

  useEffect(() => {
    if (status === "loading") return;

    if (isAuthenticated && isPublicPath) {
      // Middleware sends expired users to "/" while this tab may still hold
      // a stale session, so confirm with the server before leaving the login page
      getSession().then((fresh) => {
        if (fresh) router.replace("/dashboard"); // logged in → away from login page
        else window.location.replace("/");       // expired → reload to show login
      });
    } else if (!isAuthenticated && !isPublicPath) {
      router.replace("/");            // not logged in → back to login
    }
  }, [status, isAuthenticated, isPublicPath, router]);

  // When the session expires, re-check with the server: if it's gone,
  // reload to the login page; otherwise wait for the new expiry
  useEffect(() => {
    if (!session?.expires) return;

    let timer;
    const schedule = (expires) => {
      const delay = Math.max(new Date(expires).getTime() - Date.now(), 0) + 1000;
      timer = setTimeout(async () => {
        const fresh = await getSession();
        if (fresh?.expires) schedule(fresh.expires);
        else window.location.replace("/");
      }, delay);
    };

    schedule(session.expires);
    return () => clearTimeout(timer);
  }, [session?.expires]);

  // Show nothing while session is resolving
  if (status === "loading") return null;

  // Prevent flash: don't render protected layout for unauth users
  if (!isAuthenticated && !isPublicPath) return null;

  // Prevent flash: don't render login page for authed users
  if (isAuthenticated && isPublicPath) return null;

  // Public pages — no layout chrome
  if (!isAuthenticated) return <>{children}</>;

  // Protected layout
  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar session={session} />

      <div
        className="flex flex-col flex-1 overflow-hidden"
        style={{ paddingLeft: "calc(var(--sidebar-width, 80px) + 5px)" }}
      >
        <Header session={session} />

        <main className="flex-1 overflow-y-auto p-2 rounded-lg bg-white shadow">
          {children}
        </main>

        <Footer />
      </div>
    </div>
  );
}